/**
 * FlashReadOverlay.jsx — drop-in flash-reading widget for any long text
 * surface in the app. Feb 9 2026 user spec: *"Can you make flash reading
 * available for all readings in the app."*
 *
 * Usage:
 *   <FlashReadButton text={item.text} title={item.title} />
 *
 * Renders a small inline button that opens a full-screen flash-reading
 * overlay with WPM + chunk-size pickers (matching /reader-training and
 * /quiz-demo). Pure client-side — no backend writes — so it can be
 * dropped into anonymous reader contexts safely.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Zap, X, Play, Pause, RotateCw, ChevronLeft, ChevronRight, Square, Gauge } from 'lucide-react';
import ReadingSpeedDial from './ReadingSpeedDial';
import { authFetch } from '../utils/authUtils';
import QuitDrillDialog from './QuitDrillDialog';

// Same WPM tiers + chunk modes used elsewhere — keep them in sync.
const WPM_TIERS = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];
const CHUNK_MODES = [
  { id: 1, label: '1 word' },
  { id: 2, label: '2 words' },
  { id: 3, label: '3 words' },
];

// Condensed gear-shifter presets used by the mid-run speed dial. Mirrors
// the audio-player dial pattern in /app/frontend/src/components/PlaybackSpeedDialModal.jsx:
// a small handful of strong gear stops + step-50 fine-tune buttons.
const WPM_GEARS = [
  { label: 'Slow',    value: 100 },
  { label: 'Steady',  value: 300 },
  { label: 'Fast',    value: 500 },
  { label: 'Rapid',   value: 700 },
  { label: 'Max',     value: 1000 },
];

const Stage = {
  SETUP:   'setup',
  RUNNING: 'running',
  PAUSED:  'paused',
  DONE:    'done',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FlashReadOverlay = ({
  text = '',
  title = '',
  onClose,
  onNext = null,
  nextEpisodeTitle = '',
  passageId = '',      // Iter 81 — optional. Enables "Save & quit".
  passageTitle = '',   // stored on the bookmark row for the resume card.
}) => {
  const [stage, setStage] = useState(Stage.SETUP);
  const [wpm, setWpm] = useState(300);
  const [chunkSize, setChunkSize] = useState(1);
  const [idx, setIdx] = useState(0);
  // Feb 12 2026 user spec — speed adjustment wheel mid-flash, same
  // gear-shifter pattern as the audio/reading library players.
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  // Iter 81 — Quit confirm dialog + bookmark hydration for resume.
  const [quitOpen, setQuitOpen] = useState(false);
  const [savingQuit, setSavingQuit] = useState(false);
  const [resumeBookmark, setResumeBookmark] = useState(null);

  // Tokenise once per text — splitting again on every render would
  // thrash the timer for long passages.
  const words = useMemo(() => text.split(/\s+/).filter(Boolean), [text]);
  const chunks = useMemo(() => {
    if (!words.length) return [];
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      out.push(words.slice(i, i + chunkSize).join(' '));
    }
    return out;
  }, [words, chunkSize]);

  // Estimated read time given the user's current settings — surfaced on
  // the setup screen so the user can dial in their pace.
  const estSecs = Math.round((words.length / wpm) * 60);

  // Flash timer — paused/done states stop the schedule cleanly.
  useEffect(() => {
    if (stage !== Stage.RUNNING) return;
    if (idx >= chunks.length) { setStage(Stage.DONE); return; }
    const msPerChunk = Math.max(80, Math.round((60_000 / wpm) * chunkSize));
    const t = setTimeout(() => setIdx((i) => i + 1), msPerChunk);
    return () => clearTimeout(t);
  }, [stage, idx, chunks, wpm, chunkSize]);

  // Keyboard shortcuts: space = play/pause, esc = close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose?.(); return; }
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        setStage((s) => (
          s === Stage.RUNNING ? Stage.PAUSED :
          s === Stage.PAUSED  ? Stage.RUNNING :
          s === Stage.SETUP   ? Stage.RUNNING :
          s
        ));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const start = () => { setIdx(0); setStage(Stage.RUNNING); };
  const restart = () => { setIdx(0); setStage(Stage.RUNNING); };
  const pct = chunks.length ? Math.min(100, Math.round((idx / chunks.length) * 100)) : 0;

  // Iter 81 — hydrate a flash bookmark for this passage so the setup
  // screen can offer a Resume button. `passageId` is optional; when
  // absent the Save & Quit path stays hidden (component still works).
  useEffect(() => {
    if (!passageId) return;
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/drill-bookmarks`);
        if (!r.ok) return;
        const d = await r.json();
        const hit = (d.bookmarks || []).find(
          (b) => b.drill_kind === 'flash' && b.passage_id === passageId,
        );
        if (alive) setResumeBookmark(hit || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [passageId]);

  const resumeFromBookmark = () => {
    if (!resumeBookmark) return;
    setWpm(resumeBookmark.wpm);
    setChunkSize(resumeBookmark.chunk_size);
    setIdx(resumeBookmark.chunk_idx || 0);
    setStage(Stage.RUNNING);
    authFetch(
      `${BACKEND_URL}/api/learn/drill-bookmarks/flash/${resumeBookmark.passage_id}`,
      { method: 'DELETE' },
    ).catch(() => {});
    setResumeBookmark(null);
  };

  const doSaveAndQuit = async () => {
    if (!passageId) { onClose?.(); return; }
    setSavingQuit(true);
    try {
      await authFetch(`${BACKEND_URL}/api/learn/drill-bookmarks/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          drill_kind: 'flash',
          passage_id: passageId,
          passage_title: passageTitle || title || '',
          wpm,
          chunk_size: chunkSize,
          chunk_idx: idx,
          total_chunks: chunks.length || 1,
        }),
      });
    } catch { /* non-fatal */ }
    finally {
      setSavingQuit(false);
      setQuitOpen(false);
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#0B0C10] text-white flex flex-col"
      data-testid="flash-overlay"
      style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
    >
      {/* HUD strip */}
      <div className="px-5 py-3 flex items-center justify-between border-b border-white/5">
        <button
          type="button"
          onClick={() => {
            // Iter 81 — if running/paused, ask before losing state.
            // In setup stage there's nothing to lose, so bail directly.
            if ((stage === Stage.RUNNING || stage === Stage.PAUSED) && passageId) {
              setQuitOpen(true);
            } else {
              onClose?.();
            }
          }}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-black text-red-300 hover:text-red-200 border border-red-500/40 hover:border-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-full px-3 py-1.5 transition-colors"
          data-testid="flash-overlay-close"
          title="Quit flash read (Esc)"
        >
          <X className="w-3.5 h-3.5" /> Quit
        </button>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Flash Read</span>
        <div className="text-right">
          <p className="text-[9px] uppercase tracking-widest text-white/40">{wpm} wpm · {chunkSize}w</p>
        </div>
      </div>

      {/* SETUP */}
      {stage === Stage.SETUP && (
        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8 max-w-2xl w-full mx-auto space-y-6" data-testid="flash-overlay-setup">
          {resumeBookmark && (
            <div
              className="rounded-2xl bg-amber-500/10 border border-amber-400/40 p-4 flex items-center gap-3"
              data-testid="flash-overlay-resume-banner"
            >
              <div className="flex-1 min-w-0">
                <p className="eyebrow text-amber-200 mb-0.5">Resume where you left off</p>
                <p className="text-sm text-white font-medium truncate">
                  {resumeBookmark.progress_pct}% in · {resumeBookmark.wpm} wpm · {resumeBookmark.chunk_size}w chunks
                </p>
              </div>
              <button
                type="button"
                onClick={resumeFromBookmark}
                className="rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 px-3 py-2 text-xs font-black uppercase tracking-widest"
                data-testid="flash-overlay-resume-btn"
              >
                Resume
              </button>
            </div>
          )}
          {title && (
            <div>
              <p className="eyebrow text-amber-300">Flash reading</p>
              <h2
                className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight mt-1"
              >
                {title}
              </h2>
            </div>
          )}
          <p className="text-sm text-white/65">
            <strong className="text-white">{words.length}</strong> words ·
            est. <strong className="text-white">{estSecs}s</strong> at <strong className="text-amber-300">{wpm} wpm</strong> in <strong className="text-amber-300">{chunkSize}-word chunks</strong>
          </p>

          {/* WPM tier picker */}
          <section data-testid="flash-overlay-wpm-picker">
            <p className="eyebrow text-white/40 mb-2">Reading speed</p>
            <div className="grid grid-cols-5 gap-1.5">
              {WPM_TIERS.map((t) => {
                const active = wpm === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setWpm(t)}
                    className={`text-center rounded-xl border px-1 py-2 transition ${
                      active
                        ? 'border-amber-400 bg-amber-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`flash-overlay-wpm-${t}`}
                  >
                    <span className="text-sm font-black tabular-nums">{t}</span>
                    <span className="block text-[9px] uppercase tracking-widest text-white/40">wpm</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Chunk picker */}
          <section data-testid="flash-overlay-chunk-picker">
            <p className="eyebrow text-white/40 mb-2">Chunk size</p>
            <div className="grid grid-cols-3 gap-2">
              {CHUNK_MODES.map((c) => {
                const active = chunkSize === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setChunkSize(c.id)}
                    className={`text-left rounded-xl border px-3 py-2 transition ${
                      active
                        ? 'border-amber-400 bg-amber-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`flash-overlay-chunk-${c.id}`}
                  >
                    <span className="text-base font-black">{c.id}<span className="text-[9px] uppercase tracking-widest text-white/40 ml-1">w</span></span>
                    <p className="text-[10px] text-white/50 leading-tight mt-0.5">{c.label}</p>
                  </button>
                );
              })}
            </div>
          </section>

          <button
            type="button"
            onClick={start}
            className="w-full sm:w-auto bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black px-8 py-4 rounded-full uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[0_0_40px_rgba(255,59,48,0.35)]"
            data-testid="flash-overlay-start"
          >
            Start flash read
            <Play className="w-4 h-4" />
          </button>
          <p className="text-[10px] uppercase tracking-widest text-white/30">
            Tip: tap <kbd className="px-1 py-0.5 bg-white/5 rounded">space</kbd> to pause/resume · <kbd className="px-1 py-0.5 bg-white/5 rounded">esc</kbd> to close
          </p>
        </div>
      )}

      {/* RUNNING / PAUSED */}
      {(stage === Stage.RUNNING || stage === Stage.PAUSED) && (
        <div className="flex-1 flex flex-col" data-testid={`flash-overlay-${stage}`}>
          <div className="h-1.5 w-full bg-white/5">
            <div
              className="h-full bg-amber-400 transition-[width] duration-100"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <span
              className={`font-black text-white text-center break-words ${
                chunkSize === 1 ? 'text-5xl sm:text-7xl md:text-8xl' :
                chunkSize === 2 ? 'text-4xl sm:text-6xl md:text-7xl' :
                                   'text-3xl sm:text-5xl md:text-6xl'
              }`}
              style={{ fontFamily: '"Fraunces", Georgia, serif', letterSpacing: '-0.02em' }}
              data-testid="flash-overlay-current"
            >
              {chunks[idx] || ''}
            </span>
          </div>
          {/* Mid-flash speed dial — Feb 12 2026 user spec: "Give me the
              speed adjustment wheel on the flash reading player use
              same library style and pattern". Re-uses ReadingSpeedDial
              (gear-shifter) so flash matches the audio/library players. */}
          {showSpeedDial && (
            <div className="px-3 sm:px-5 pb-2" data-testid="flash-overlay-speed-dial-wrap">
              <ReadingSpeedDial
                value={wpm}
                onChange={(v) => setWpm(v)}
                min={50}
                max={1200}
                step={50}
                presets={WPM_GEARS}
                format={(v) => `${v} wpm`}
                accentClass="bg-amber-400"
                accentBorderClass="border-amber-400"
                accentTextClass="text-amber-300"
                testIdPrefix="flash"
              />
            </div>
          )}
          {/* Bottom transport */}
          <div className="px-5 py-4 flex items-center justify-between gap-3 border-t border-white/5">
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 5))}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center"
              data-testid="flash-overlay-back"
              title="Back 5 chunks"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setStage((s) => s === Stage.RUNNING ? Stage.PAUSED : Stage.RUNNING)}
              className="w-12 h-12 rounded-full bg-amber-400 hover:bg-amber-300 text-black inline-flex items-center justify-center"
              data-testid="flash-overlay-toggle"
            >
              {stage === Stage.RUNNING ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.min(chunks.length - 1, i + 5))}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center"
              data-testid="flash-overlay-fwd"
              title="Forward 5 chunks"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {/* Speed indicator — now a tappable pill that toggles the
                gear-shifter dial above. Mirrors how /reader-training and
                the audio player surface their dials. */}
            <button
              type="button"
              onClick={() => setShowSpeedDial((v) => !v)}
              className={`ml-auto inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition ${
                showSpeedDial
                  ? 'border-amber-400 bg-amber-400/15 text-amber-200'
                  : 'border-white/10 bg-white/[0.02] text-white/80 hover:border-white/30'
              }`}
              data-testid="flash-overlay-speed-toggle"
              aria-pressed={showSpeedDial}
              aria-label="Adjust reading speed"
            >
              <Gauge className="w-3.5 h-3.5" />
              <span className="text-right font-mono leading-tight">
                <span className="block text-[9px] uppercase tracking-widest opacity-60">at</span>
                <span className="block text-base font-black tabular-nums">{wpm}</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* DONE */}
      {stage === Stage.DONE && (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6" data-testid="flash-overlay-done">
          <p className="eyebrow text-emerald-300">Finished</p>
          <h2
            className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight text-center"
          >
            {words.length} words at {wpm} wpm
          </h2>
          {/* Feb 12 2026 — if a next-episode callback is provided, show
              a countdown CTA that auto-advances. Lets the user pause
              the chain by tapping × on the countdown. */}
          {onNext && <FlashCountdown nextTitle={nextEpisodeTitle} onComplete={onNext} />}
          <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
            <button
              type="button"
              onClick={restart}
              className="bg-amber-400 hover:bg-amber-300 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="flash-overlay-restart"
            >
              <RotateCw className="w-4 h-4" /> Run again
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-white/20 text-white hover:bg-white/5 font-bold py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="flash-overlay-done-close"
            >
              <Square className="w-4 h-4" /> Done
            </button>
          </div>
        </div>
      )}

      <QuitDrillDialog
        open={quitOpen}
        onOpenChange={setQuitOpen}
        progressPct={pct}
        drillLabel="flash read"
        saving={savingQuit}
        onQuitOnly={() => { setQuitOpen(false); onClose?.(); }}
        onSaveAndQuit={doSaveAndQuit}
      />
    </div>
  );
};

/**
 * Inline trigger pill — drop next to any long-text block to expose
 * flash reading. Renders nothing if there's not enough text to bother
 * flashing (< 20 words).
 */
export const FlashReadButton = ({ text = '', title, className = '', minWords = 20, testid = 'flash-read-btn', label = 'Flash read', onNext = null, nextEpisodeTitle = '' }) => {
  const [open, setOpen] = useState(false);
  // We don't tokenize unless necessary — keeps the cost down on lists
  // that render many of these buttons (e.g. Personal Library rows).
  const wordCount = (text || '').split(/\s+/).filter(Boolean).length;
  if (wordCount < minWords) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 hover:border-amber-400/60 hover:bg-amber-400/15 text-amber-300 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${className}`}
        data-testid={testid}
        title={`Flash-read this passage (${wordCount} words)`}
      >
        <Zap className="w-3 h-3" />
        {label}
      </button>
      {open && (
        <FlashReadOverlay
          text={text}
          title={title}
          onClose={() => setOpen(false)}
          onNext={onNext ? () => { setOpen(false); onNext(); } : null}
          nextEpisodeTitle={nextEpisodeTitle}
        />
      )}
    </>
  );
};

export default FlashReadOverlay;

/** Inline countdown sub-component — shown on the DONE screen when an
 *  `onNext` callback is provided. Counts 3 → 2 → 1 then fires the
 *  callback. User can cancel via the × button. Feb 12 2026 user spec. */
const FlashCountdown = ({ nextTitle, onComplete }) => {
  const [n, setN] = useState(3);
  const [active, setActive] = useState(true);
  useEffect(() => {
    if (!active) return;
    if (n <= 0) {
      const t = setTimeout(() => onComplete?.(), 400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setN(n - 1), 1000);
    return () => clearTimeout(t);
  }, [n, active, onComplete]);
  if (!active) return null;
  return (
    <div
      className="w-full max-w-sm rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4 text-center space-y-1"
      data-testid="flash-overlay-countdown"
    >
      <p className="text-[10px] uppercase tracking-[0.4em] text-amber-200/80">Next episode in</p>
      <p
        className="font-display text-6xl font-black text-amber-300 tabular-nums"
        data-testid="flash-overlay-countdown-number"
      >
        {n > 0 ? n : 'GO'}
      </p>
      {nextTitle && <p className="text-xs text-white/70 truncate">Up next: {nextTitle}</p>}
      <button
        type="button"
        onClick={() => setActive(false)}
        className="mt-1 text-[10px] uppercase tracking-widest text-white/60 hover:text-white"
        data-testid="flash-overlay-countdown-cancel"
      >
        Cancel auto-advance
      </button>
    </div>
  );
};
